// The static indexed triangle mesh, on WebGPU (the runtime design §1).
//
// It is the twin of `gl/mesh.ts`, and `gl/mesh.ts` states why the engine
// needs it: the traced territory outline of R495 has a different value at
// every vertex and shares vertices between triangles, which a divisor-1
// batch cannot draw.
//
// The WebGPU half is two buffers and no vertex array object. The index
// format is `uint32`, because the outline of a whole faction passes 65 535
// vertices. The geometry is rebuilt whole on every owner change, so there is
// no partial write.
//
// Like every other GPU object of the engine it survives a device loss: the
// two CPU arrays are kept, and `create` builds the buffers again from them.

import { BUFFER_COPY_DST, BUFFER_INDEX, BUFFER_VERTEX } from './flags';
import type { AttribSpec, MeshBuffer as MeshBufferApi } from '../gfx/types';
import type { Gpu, GpuResource } from './context';

export class GpuMeshBuffer implements GpuResource, MeshBufferApi {
  private vertices: Float32Array | null = null;
  private indices: Uint32Array | null = null;
  private vbo: GPUBuffer | null = null;
  private ibo: GPUBuffer | null = null;

  // The attribute list rides the pipeline's vertex layout, so the mesh
  // takes it for the one factory signature and reads nothing from it.
  constructor(
    private readonly gpu: Gpu,
    readonly floatsPerVertex: number,
    _attribs: readonly AttribSpec[],
  ) {
    gpu.register(this);
  }

  create(device: GPUDevice): void {
    this.destroy();
    if (!this.vertices || !this.indices) return;
    this.upload(device, this.vertices, this.indices);
  }

  destroy(): void {
    if (this.vbo) this.vbo.destroy();
    if (this.ibo) this.ibo.destroy();
    this.vbo = null;
    this.ibo = null;
  }

  /**
   * Replaces the geometry and uploads it.
   *
   * Both arrays are kept on the instance, so `create` refills the buffers
   * after a device restore. A call while the device is lost keeps the arrays
   * and uploads nothing; the restore then does the upload.
   */
  set(vertices: Float32Array, indices: Uint32Array): void {
    this.vertices = vertices;
    this.indices = indices;
    if (this.gpu.lost) return;
    this.upload(this.gpu.device, vertices, indices);
  }

  /** How many indices the last `set` gave. It is 0 before the first one. */
  get indexCount(): number {
    return this.indices ? this.indices.length : 0;
  }

  /**
   * Draws the mesh as one indexed call. Returns 1, or 0 when there is
   * nothing to draw or the device is lost.
   */
  draw(): number {
    const gpu = this.gpu;
    const n = this.indexCount;
    if (n === 0 || !this.vbo || !this.ibo || gpu.lost) return 0;
    const pass = gpu.current?.bindDraw();
    if (!pass) return 0;
    pass.setVertexBuffer(0, this.vbo);
    pass.setIndexBuffer(this.ibo, 'uint32');
    pass.drawIndexed(n, 1);
    gpu.drawCalls++;
    gpu.instances++;
    return 1;
  }

  dispose(): void { this.gpu.unregister(this); }

  /**
   * Builds both buffers at the size the arrays ask for and fills them.
   *
   * A WebGPU buffer has no resize, so a mesh that grew takes a new pair. The
   * size is rounded up to four bytes, which is what `writeBuffer` wants.
   */
  private upload(device: GPUDevice, vertices: Float32Array, indices: Uint32Array): void {
    this.destroy();
    this.vbo = device.createBuffer({
      label: 'fast: mesh vertices',
      size: Math.max(4, vertices.byteLength),
      usage: BUFFER_VERTEX | BUFFER_COPY_DST,
    });
    this.ibo = device.createBuffer({
      label: 'fast: mesh indices',
      size: Math.max(4, indices.byteLength),
      usage: BUFFER_INDEX | BUFFER_COPY_DST,
    });
    device.queue.writeBuffer(this.vbo, 0, vertices.buffer, vertices.byteOffset, vertices.byteLength);
    device.queue.writeBuffer(this.ibo, 0, indices.buffer, indices.byteOffset, indices.byteLength);
  }
}
